import { useCallback, useEffect, useRef } from 'react';
import { useAnalyticsPackage, PackageItem } from '../context/AnalyticsPackageContext';
import { useApi } from '../hooks/useApi';

interface ApiLensItemDto {
  entityType: string;
  entityId: number;
  entityName: string;
  chartName: string;
  chartTitle: string;
  propertyType: string | null;
  buildingCategoryId: number | null;
  yearStart: number | null;
  yearEnd: number | null;
  subareaId: number | null;
  subareaName: string | null;
  sortKey: string | null;
  hiddenSeries?: string[];
}

function toApiItem(item: PackageItem): ApiLensItemDto {
  return {
    entityType: item.entityType,
    entityId: item.entityId,
    entityName: item.entityName,
    chartName: item.chartName,
    chartTitle: item.chartTitle,
    propertyType: item.propertyType,
    buildingCategoryId: item.buildingCategoryId,
    yearStart: item.yearStart,
    yearEnd: item.yearEnd,
    subareaId: item.subareaId,
    subareaName: item.subareaName,
    sortKey: item.sortOrder,
    hiddenSeries: item.hiddenSeries,
  };
}

function fromApiItem(i: ApiLensItemDto): PackageItem {
  const key = [
    i.entityType, i.entityId, i.chartName,
    i.propertyType ?? '', i.buildingCategoryId ?? '',
    i.yearStart ?? '', i.yearEnd ?? '',
    i.subareaId ?? '',
    i.sortKey ?? '',
  ].join('|');
  return {
    key,
    entityType: i.entityType as PackageItem['entityType'],
    entityId: i.entityId,
    entityName: i.entityName,
    chartName: i.chartName,
    chartTitle: i.chartTitle,
    propertyType: i.propertyType,
    buildingCategoryId: i.buildingCategoryId,
    yearStart: i.yearStart,
    yearEnd: i.yearEnd,
    subareaId: i.subareaId,
    subareaName: i.subareaName,
    sortOrder: i.sortKey,
    hiddenSeries: i.hiddenSeries?.length ? i.hiddenSeries : undefined,
  };
}

export function LensSyncManager() {
  const { items, activeLensId, setItemsFromLoad } = useAnalyticsPackage();
  const { authFetch, isAuthenticated } = useApi();

  const initialLoadDone = useRef(false);
  const suppressSync = useRef(false);
  const itemsRef = useRef(items);
  const activeLensIdRef = useRef(activeLensId);

  useEffect(() => { itemsRef.current = items; }, [items]);
  useEffect(() => { activeLensIdRef.current = activeLensId; }, [activeLensId]);

  const doSync = useCallback(() => {
    authFetch('/api/lenses/current', {
      method: 'PUT',
      body: JSON.stringify({
        items: itemsRef.current.map(toApiItem),
        activeLensId: activeLensIdRef.current,
      }),
    }).catch(() => {});
  }, [authFetch]);

  // On auth ready: restore from DB, or push local state up if no DB record
  useEffect(() => {
    if (!isAuthenticated) return;
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;

    authFetch('/api/lenses/current')
      .then(res => {
        if (res.status === 404) { doSync(); return null; }
        if (!res.ok) return null;
        return res.json();
      })
      .then((data: any) => {
        if (!data) return;
        suppressSync.current = true;
        setItemsFromLoad(
          (data.items as ApiLensItemDto[]).map(fromApiItem),
          data.activeLensId ?? null,
          data.activeLensName ?? null,
        );
      })
      .catch(() => {});
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced PUT on state change
  useEffect(() => {
    if (!isAuthenticated || !initialLoadDone.current) return;
    if (suppressSync.current) { suppressSync.current = false; return; }
    const timer = setTimeout(doSync, 2000);
    return () => clearTimeout(timer);
  }, [items, activeLensId, isAuthenticated, doSync]);

  return null;
}
